// How one note says who wrote it and when (spec 09 §4.2's E2-2a1 annotation,
// D-323 rulings 1 to 3). Shared by the header's pinned note and the Notes
// panel, so the two never word one note differently.
//
//   * A typed note names its author and the day it was written.
//   * A legacy note, copied from the customer's old notes field by the
//     migration, has no known author, and says "carried over" instead.
//   * A redacted note keeps its author, its time and its pin, and its body is
//     the server's fixed marker. The marker is never painted: the note reads
//     "Text removed by … on …" in the reader's language.

import { useTranslation } from "react-i18next";
import { StatusChip } from "../../../../../components/status-chip";
import { formatTimestamp } from "../../../../../lib/datetime";
import type { CustomerNote } from "../../hooks/crm-queries";

export function isRedacted(note: Pick<CustomerNote, "redacted_at">): boolean {
  return note.redacted_at !== null;
}

/** The note's words, or the sentence that says they were removed. */
export function NoteBody({ note }: { note: CustomerNote }) {
  const { t, i18n } = useTranslation();
  if (note.redacted_at !== null) {
    return (
      <p className="sales-note-body sales-note-redacted" data-testid="sales-note-redacted">
        {note.redacted_by_name !== null
          ? t("sales.notes.redactedBy", {
              name: note.redacted_by_name,
              date: formatTimestamp(note.redacted_at, i18n.language),
            })
          : t("sales.notes.redactedOn", {
              date: formatTimestamp(note.redacted_at, i18n.language),
            })}
      </p>
    );
  }
  return (
    <p className="sales-note-body" data-testid="sales-note-body">
      {note.body}
    </p>
  );
}

/** Author and date, or the carried-over chip for a legacy note. */
export function NoteMeta({ note }: { note: CustomerNote }) {
  const { t, i18n } = useTranslation();
  const when = formatTimestamp(note.created_at, i18n.language);
  if (note.origin === "legacy") {
    return (
      <span className="sales-note-meta">
        <StatusChip
          variant="neutral"
          dot={false}
          data-testid="sales-note-carried-over"
          className="sales-note-carried"
        >
          {t("sales.notes.carriedOver")}
        </StatusChip>
      </span>
    );
  }
  return (
    <span className="sales-note-meta" data-testid="sales-note-meta">
      {t("sales.notes.byOn", {
        name: note.author_name ?? t("sales.notes.formerMember"),
        date: when,
      })}
    </span>
  );
}
